import { useState } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { getNotificationMessageByType } from 'lib/notifications';
import { Button, TextWithLineBreaks, TimeIndicator } from 'components/common';
import { DropdownLink } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';

import type { Notification as INotification } from 'shared-types';

interface Props {
  notification: INotification;
  onClick: (id: INotification['id']) => Promise<void>;
}

const Notification = ({ notification, onClick }: Props) => {
  const {
    language,
    messages,
    serverConfig: { accountGuestUseName, userUseName },
    theme
  } = useReduxState();

  const [showMore, setShowMore] = useState(false);

  const {
    associatedUser,
    achievement: { title: achievementTitle },
    calendarEvent: { title: eventTitle },
    competency: { name: competencyTitle },
    course: { title: courseTitle },
    data,
    group: { title: groupTitle },
    id,
    isRead,
    link,
    individualAchievement: { title: individualAchievementTitle },
    path: { pathTitle },
    pathStep: { number },
    relatedId,
    type
  } = notification;

  let user = messages.commonAnonymous;
  if (associatedUser) {
    const { email, firstName, lastName, roles, username } = associatedUser;

    user =
      ((!roles?.includes('guest') && userUseName) ||
        (roles?.includes('guest') && accountGuestUseName)) &&
      firstName &&
      lastName
        ? `${firstName} ${lastName}`
        : roles?.includes('guest')
        ? `${messages.usersGuest}${associatedUser.id}`
        : email
        ? email
        : username;
  }

  let numberOfUsers: number;
  let queryExtension = '';
  if (
    type === 'answer-review' ||
    type === 'answer-comment' ||
    type === 'answer-request-changes'
  ) {
    queryExtension = `?blockId=${relatedId}`;
  } else if (type === 'discussion-comment') {
    numberOfUsers = data.discussionRepliesId.length;
    queryExtension = `?blockId=${relatedId}&parentCommentId=${data.discussionParentCommentId}&commentId=${data.discussionRepliesId[0]}`;
  }

  const [notificationHeader, notificationInfoMessage] =
    getNotificationMessageByType({
      messages,
      isMultiUser: numberOfUsers > 1,
      type
    });

  let notificationText = notificationInfoMessage
    ? getMessageWithPlaceholders({
        message: notificationInfoMessage,
        placeholders: {
          achievementTitle,
          competencyTitle,
          courseTitle,
          eventTitle,
          groupTitle,
          individualAchievementTitle,
          numberOfUsers,
          pathTitle,
          stepNumber: number,
          user
        }
      })
    : 'unknown event type';

  if (
    ['course-deadline', 'path-course-deadline', 'path-step-deadline'].includes(
      type
    )
  ) {
    const dueIn = data.months
      ? getMessageWithPlaceholders({
          message: messages.notificationsDeadlineIsDueInMonths,
          placeholders: { numberOfMonths: data.months }
        })
      : getMessageWithPlaceholders({
          message: messages.notificationsDeadlineIsDueInWeeks,
          placeholders: { numberOfWeeks: data.weeks }
        });
    notificationText = `${notificationText} ${dueIn}`;
  }

  const handleLinkFollow = () => {
    if (!isRead) onClick(id);
  };

  const handleMarkAsRead = async (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    await onClick(id);
  };

  const handleShowMore = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setShowMore(!showMore);
  };

  const dataMessage = data?.messageLocales?.[language] || data?.message || null;

  const renderItem = () => (
    <div className="item">
      <div>
        <div className="header" style={{ fontWeight: isRead ? 600 : 700 }}>
          {notificationHeader}
        </div>
        <div className="timestamp">
          <TimeIndicator language={language} timeString={notification.time} />
        </div>

        <div className="content" style={{ fontWeight: isRead ? 400 : 600 }}>
          {notificationText}
        </div>
      </div>

      <div className="mark-read">
        <Button
          category="transparent"
          data-keep-open={true}
          icon="check-circle"
          onClick={handleMarkAsRead}
          size="large"
          style={{ width: 28, height: 28, padding: 0 }}
          {...(isRead
            ? {
                'aria-label': messages.notificationsMarkedAsRead,
                'data-tooltip': messages.notificationsMarkedAsRead,
                disabled: true
              }
            : {
                'aria-label': messages.notificationsMarkAsRead,
                'data-tooltip': messages.notificationsMarkAsRead
              })}
        />
      </div>

      {dataMessage ? (
        <div className="message">
          <Button
            category="transparent"
            data-keep-open={true}
            onClick={handleShowMore}
            size="small"
            style={{ fontSize: '1.3rem', fontWeight: 600, padding: 0 }}
            text={
              <>
                {showMore
                  ? messages.notificationsHideMessage
                  : messages.notificationsShowMessage}

                <Icon
                  icon={showMore ? 'chevron-up' : 'chevron-down'}
                  style={{ fontSize: '0.9rem', marginLeft: 5 }}
                />
              </>
            }
          />

          {showMore ? (
            <div className="message-content">
              <TextWithLineBreaks text={dataMessage} />
            </div>
          ) : (
            ''
          )}
        </div>
      ) : null}

      <style jsx>{`
        .item {
          padding: 5px 0;
          position: relative;
        }
        .header {
          color: ${theme.textColor};
          font-size: 1.4rem;
          padding-right: 30px;
        }
        .timestamp {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 400;
          margin: 3px 0;
        }
        .content {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          white-space: break-spaces;
        }
        .mark-read {
          position: absolute;
          top: 0;
          right: 0;
        }
        .message {
          align-items: 'flex-start';
          display: 'flex';
          flex-direction: 'column';
          margin-top: 5px;
        }
        .message > button:hover {
          text-decoration: underline;
        }
        .message-content {
          font-style: italic;
          font-weight: 400;
          margin-top: 3px;
        }
      `}</style>
    </div>
  );

  return (
    <DropdownLink
      href={`${link}${queryExtension}`}
      onClick={handleLinkFollow}
      style={{ maxWidth: 500, padding: '4px 5px' }}
      text={renderItem()}
    />
  );
};

export default Notification;
